/**
 * **The AOD assessment package** (Stream C · C01, Documents.dc.html; due November 15): the
 * festival's statement to the Archdiocese, laid out as drawn — the cover letter with its five
 * figures and three signature lines, then the P&L two ways, the books as kept beside the 2027
 * chart, with the same totals. The drawing says three pages and draws two; the third waits on
 * the canvas and is never invented here.
 *
 * Nothing is computed that another engine already computes. The year's figures are the
 * distributions engine's (Money's one-pager reads them); the two views are Money's statement
 * (WORKLIST M16 — the old account beside its v3 home, festival expenses apart from the
 * assessment and the distributions). This only lays them out: it picks the largest accounts to
 * fit the page, puts the rest on one "all other" line, and says so in words when the two views
 * or the two pages disagree. A figure it was not handed prints NO DATA, never a sample.
 */
import { dollars } from './dollars.js';
import { NO_DATA, type PaperBlock, type PaperCell, type PaperPage } from './paper_page.js';

export { NO_DATA };

/** The year as the distributions engine computes it (Money's one-pager, "The year"). */
export interface AodYear {
  incomeCents: number;
  festivalExpensesCents: number;
  netProceedsCents: number;
  aodCents: number;
  distributedCents: number;
  retainedCents: number;
}

/** The Year setting: a rate in basis points of net proceeds, or an amount; null when unset. */
export interface AodRate {
  bps: number | null;
  cents: number | null;
}

export interface TwoViewAccount {
  number: string;
  name: string;
}
/** One old account as it was posted, its v3 home (null when it has none yet), and its amount. */
export interface TwoViewRow {
  qbo: TwoViewAccount;
  v3: TwoViewAccount | null;
  cents: number;
}
export interface TwoViewTotals {
  incomeCents: number;
  festivalExpensesCents: number;
}
/**
 * **Money's statement two ways** (WORKLIST M16, `GET /money/statement-two-views/:fy`): the
 * year's income and festival-expense accounts by QuickBooks account as posted, each with its v3
 * home; each view's own totals; and the books' facts the cover letter states.
 */
export interface TwoViewStatement {
  fiscalYear: number;
  income: TwoViewRow[];
  /** Festival expenses only — the assessment and the distributions are on page 1. */
  expense: TwoViewRow[];
  qbo: TwoViewTotals;
  v3: TwoViewTotals;
  books: { firstFiscalYear: number | null; entries: number; reconciledThrough: string | null };
}

export type AodCell = PaperCell;
export type AodBlock = PaperBlock;
export type AodPage = PaperPage;

export interface AodInput {
  fiscalYear: number;
  /** null: the one-pager did not answer, or answered NO DATA. */
  year: AodYear | null;
  /** undefined: the Year setting could not be read; null: it is not set. */
  rate: AodRate | null | undefined;
  /** null: Money's two-view statement is not there yet (M16), or did not answer. */
  statement: TwoViewStatement | null;
  /** The package's due date from the finance year (`YYYY-MM-DD`), or null. */
  due: string | null;
  /** The two named signers from Roles; null prints the label alone, for a hand to write in. */
  director: string | null;
  financeDirector: string | null;
}

/** The page fits seven income lines and six expense lines, the "all other" line among them. */
export const INCOME_LINES = 7;
export const EXPENSE_LINES = 6;

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
/** `2026-11-15` → `November 15, 2026` — a date string as the letter writes it, no clock read. */
export function letterDate(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return `${MONTHS[(m ?? 1) - 1] ?? ''} ${String(d ?? '')}, ${String(y ?? '')}`;
}
/** `2026-06-30` → `June 30`. */
const monthDay = (ymd: string): string => letterDate(ymd).replace(/, \d+$/, '');

/** `(7%)`, `(7.5%)`, `(a set amount)`, or NO DATA when the Year setting is not set. */
export function rateWords(rate: AodRate | null | undefined): string {
  if (rate?.bps != null) return `(${String(rate.bps / 100)}%)`;
  if (rate?.cents != null) return '(a set amount)';
  return `(rate ${NO_DATA})`;
}

const account = (a: TwoViewAccount): string => `${a.number} · ${a.name}`;

/** `remaining 4xxx` or `remaining 5xxx – 7xxx`, from the v3 numbers the "all other" line holds. */
export function remainingRange(rows: readonly TwoViewRow[]): string {
  const firsts = [
    ...new Set(rows.map((r) => (r.v3 ?? r.qbo).number.slice(0, 1)).filter(Boolean)),
  ].sort();
  const lo = firsts[0];
  const hi = firsts[firsts.length - 1];
  if (!lo || !hi) return 'remaining';
  return lo === hi ? `remaining ${lo}xxx` : `remaining ${lo}xxx – ${hi}xxx`;
}

/**
 * The largest accounts that fit, in the order the books keep them (QuickBooks' own numbers), then
 * one line for the rest. Nothing is dropped: the "all other" line carries every account not
 * named, so the column adds to its total.
 */
export function fitRows(rows: readonly TwoViewRow[], lines: number, other: string): AodCell[][] {
  if (rows.length <= lines)
    return [...rows]
      .sort(asKept)
      .map((r) => [r.qbo.name, r.cents, r.v3 ? account(r.v3) : 'no v3 home yet', r.cents]);
  const big = [...rows].sort((a, b) => b.cents - a.cents || asKept(a, b));
  const named = big.slice(0, lines - 1).sort(asKept);
  const rest = big.slice(lines - 1);
  const restCents = rest.reduce((n, r) => n + r.cents, 0);
  return [
    ...named.map((r) => [r.qbo.name, r.cents, r.v3 ? account(r.v3) : 'no v3 home yet', r.cents]),
    [other, restCents, remainingRange(rest), restCents],
  ];
}
function asKept(a: TwoViewRow, b: TwoViewRow): number {
  return a.qbo.number.localeCompare(b.qbo.number) || a.qbo.name.localeCompare(b.qbo.name);
}

/** The two pages the drawing draws, filled. */
export function aodPackage(i: AodInput): AodPage[] {
  const fy = String(i.fiscalYear);
  const yearEnd = `${fy}-06-30`;
  const y = i.year;
  const s = i.statement;
  const fig = (cents: number | undefined): AodCell => cents ?? NO_DATA;

  const books = s
    ? [
        s.books.firstFiscalYear === null
          ? `${NO_DATA} years`
          : `${String(i.fiscalYear - s.books.firstFiscalYear + 1)} years`,
        `${String(s.books.entries).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} transactions in FY${fy}`,
        s.books.reconciledThrough
          ? `reconciled through ${monthDay(s.books.reconciledThrough)}`
          : `reconciled through ${NO_DATA}`,
      ].join(' · ')
    : `${NO_DATA} — the statement two ways is not in Money yet (WORKLIST M16)`;

  // Page 1's figures and page 2's totals are two engines' answers to one question.
  const pagesDiffer =
    y &&
    s &&
    (y.incomeCents !== s.qbo.incomeCents || y.festivalExpensesCents !== s.qbo.festivalExpensesCents)
      ? `The statement’s totals (${dollars(s.qbo.incomeCents)} in, ${dollars(s.qbo.festivalExpensesCents)} out) are not the year’s (${dollars(y.incomeCents)} in, ${dollars(y.festivalExpensesCents)} out). The package does not go out until they agree.`
      : undefined;

  const cover: AodPage = {
    title: `Saint Lawrence Apple Fest · FY${fy}`,
    sub: `To the Archdiocese of Detroit · Parish Festivals · ${i.due ? letterDate(i.due) : NO_DATA} · from Saint Lawrence Parish, Utica`,
    blocks: [
      {
        head: '',
        cols: '1fr',
        rows: [
          [
            `Enclosed is the festival’s statement for the fiscal year ended ${letterDate(yearEnd)}, shown two ways: by the account names used in QuickBooks through ${fy}, and by the chart the festival adopted for ${String(i.fiscalYear + 1)}. The totals are the same. The assessment is computed on income after expenses at the rate the Archdiocese set.`,
          ],
          [`Books: ${books}.`],
        ],
      },
      {
        head: 'THE FOUR FIGURES',
        cols: '1fr 160px',
        rows: [
          ['Income', fig(y?.incomeCents)],
          ['Expenses', fig(y?.festivalExpensesCents)],
          ['Net before assessment and disbursements', fig(y?.netProceedsCents)],
          [
            `Assessment ${rateWords(i.rate)} + disbursements`,
            fig(y ? y.aodCents + y.distributedCents : undefined),
          ],
          ['Retained', fig(y?.retainedCents)],
        ],
        ...(pagesDiffer ? { gap: pagesDiffer } : {}),
      },
      {
        head: 'SIGNED',
        cols: '1fr 1fr',
        rows: [
          [
            `${i.director ?? ''} · Festival director`,
            `${i.financeDirector ?? ''} · Finance director`,
          ],
          [' · Pastor', ''],
        ],
      },
    ],
    foot: 'AOD package · page 1 of 2 · cover letter',
  };

  const viewsDiffer =
    s &&
    (s.qbo.incomeCents !== s.v3.incomeCents ||
      s.qbo.festivalExpensesCents !== s.v3.festivalExpensesCents)
      ? `The two views do not agree: income ${dollars(s.qbo.incomeCents)} against ${dollars(s.v3.incomeCents)}, expenses ${dollars(s.qbo.festivalExpensesCents)} against ${dollars(s.v3.festivalExpensesCents)}. An account is mapped twice or not at all — Money → Accounts.`
      : undefined;
  const missing: AodCell[][] = [
    [NO_DATA, '', 'the statement two ways is Money’s (WORKLIST M16)', ''],
  ];
  const statement: AodPage = {
    title: `P&L · FY${fy} · two views`,
    sub: 'Left: QuickBooks accounts as kept · Right: v3 chart · same totals',
    blocks: [
      {
        head: 'INCOME',
        cols: '1fr 110px 1fr 110px',
        rows: s
          ? [
              ...fitRows(s.income, INCOME_LINES, 'All other income'),
              ['TOTAL INCOME', s.qbo.incomeCents, '', s.v3.incomeCents],
            ]
          : missing,
      },
      {
        head: 'EXPENSES',
        cols: '1fr 110px 1fr 110px',
        rows: s
          ? [
              ...fitRows(s.expense, EXPENSE_LINES, 'All other expense'),
              ['TOTAL EXPENSES', s.qbo.festivalExpensesCents, '', s.v3.festivalExpensesCents],
            ]
          : missing,
        ...(viewsDiffer ? { gap: viewsDiffer } : {}),
      },
    ],
    foot: 'AOD package · page 2 of 2 · the mapping is for reporting only; nothing was re-coded',
  };
  return [cover, statement];
}

/** The year's figures from Money's one-pager, by the words on its rows; null when one is missing. */
export function yearFromOnePager(
  rows: readonly (readonly (string | number | null)[])[],
): AodYear | null {
  const at = (label: string): number | undefined => {
    const v = rows.find((r) => r[0] === label)?.[1];
    return typeof v === 'number' ? v : undefined;
  };
  const year = {
    incomeCents: at('Income'),
    festivalExpensesCents: at('Festival expenses'),
    netProceedsCents: at('Net proceeds'),
    aodCents: at('Archdiocese assessment'),
    distributedCents: at('Distributed'),
    retainedCents: at('Retained'),
  };
  return Object.values(year).every((v) => v !== undefined) ? (year as AodYear) : null;
}
